import { FundingPolicyType } from '@shared';

const SECURIAN_RESULT_NAMES = new Set(['securecare', 'securecare iii', 'securecare iv']);

function normalizeProductName(productName: string): string {
  return productName.toLowerCase().replace(/[®™©]/g, '').replace(/\s+/g, ' ').trim();
}

function isVersionedSecureCare(productName: string): boolean {
  return /\bsecurecare\s+[ivxlcdm]+\b/.test(productName);
}

function doProductNamesMatch(normalizedQuoteName: string, normalizedResultName: string): boolean {
  const quoteIsSecureCare = normalizedQuoteName.includes('securecare');
  const resultIsSecureCare = normalizedResultName.includes('securecare');

  // Securian's versioned SecureCare products share an underwriting prompt with New York Life's
  // unversioned SecureCare product name. Require a Roman-numeral version on both sides when the
  // caller cannot provide a funding policy type.
  if (quoteIsSecureCare || resultIsSecureCare) {
    return (
      isVersionedSecureCare(normalizedQuoteName) && isVersionedSecureCare(normalizedResultName)
    );
  }

  return (
    normalizedQuoteName.includes(normalizedResultName) ||
    normalizedResultName.includes(normalizedQuoteName)
  );
}

/**
 * Finds the underwriting result for a quote by matching the product name.
 * Handles trademark symbols while keeping ambiguous SecureCare products carrier-safe.
 *
 * @param productName - The product name to match
 * @param underwritingResults - Map of product names to underwriting results
 * @param isJointPolicy - Whether this is a joint/couples policy
 * @param fundingPolicyType - Stable product identifier used to disambiguate SecureCare carriers
 * @returns The matching underwriting result, or undefined if not found
 */
export function getUnderwritingResultForProduct(
  productName: string,
  underwritingResults: Record<string, UnderwritingResult> | undefined,
  isJointPolicy: boolean = false,
  fundingPolicyType?: number,
): UnderwritingResult | undefined {
  if (!underwritingResults || !productName) return undefined;

  if (
    fundingPolicyType === FundingPolicyType.secureCareThree ||
    fundingPolicyType === FundingPolicyType.secureCareFour
  ) {
    return Object.entries(underwritingResults).find(([key]) => {
      const normalizedKey = normalizeProductName(key);
      return !normalizedKey.includes('joint') && SECURIAN_RESULT_NAMES.has(normalizedKey);
    })?.[1];
  }

  if (fundingPolicyType === FundingPolicyType.nylSecureCare) return undefined;

  const normalizedQuoteName = normalizeProductName(productName);

  // Try to find a matching key in the results
  for (const [key, result] of Object.entries(underwritingResults)) {
    const normalizedKey = normalizeProductName(key);
    const productNamesMatch = doProductNamesMatch(normalizedQuoteName, normalizedKey);

    // Check if the joint status matches
    const keyIsJoint = normalizedKey.includes('joint');
    const jointStatusMatches = isJointPolicy === keyIsJoint;

    if (productNamesMatch && jointStatusMatches) {
      return result;
    }
  }

  return undefined;
}
